import { Chip } from "@scripts/react/components/Chip";
import type { ModelChange, ModelMetrics } from "../types";
import { ModelArchitecture } from "../types";
import { useCurrentModelMetrics, useMetricsLoading } from "@/store";

interface ModelChangeInfoProps {
    change: ModelChange;
    isLegacy?: boolean;
}

const architectureLabels: Record<ModelArchitecture, string> = {
    [ModelArchitecture.RF_DETR]: "RF-DETR",
    [ModelArchitecture.RF_INSTANT]: "RF Instant",
    [ModelArchitecture.SAM3]: "SAM3"
};

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="flex flex-col gap-0.5">
            <span className="text-xs font-medium text-tertiary">{label}</span>
            <span className="text-sm font-semibold text-primary">{children}</span>
        </div>
    );
}

function MetricItem({ label, value, unit = "%" }: { label: string; value: number; unit?: string }) {
    return (
        <div className="flex flex-col items-center">
            <span className="text-lg font-bold text-primary">
                {value.toFixed(1)}
                {unit}
            </span>
            <span className="text-xs font-medium text-tertiary">{label}</span>
        </div>
    );
}

function ModelMetricsDisplay({ metrics }: { metrics: ModelMetrics }) {
    // Only show metrics that are available
    const hasDetectionMetrics =
        metrics.map !== undefined ||
        metrics.precision !== undefined ||
        metrics.recall !== undefined;
    const hasClassificationMetrics = metrics.accuracy !== undefined;
    const hasSegmentationMetrics = metrics.miou !== undefined;

    if (!hasDetectionMetrics && !hasClassificationMetrics && !hasSegmentationMetrics) {
        return null;
    }

    return (
        <div className="flex flex-col gap-2">
            <span className="text-xs font-medium text-tertiary">Performance Metrics</span>
            <div className="flex justify-around rounded-lg bg-gray-50 p-3">
                {metrics.map !== undefined && <MetricItem label="mAP" value={metrics.map} />}
                {metrics.precision !== undefined && (
                    <MetricItem label="Precision" value={metrics.precision} />
                )}
                {metrics.recall !== undefined && (
                    <MetricItem label="Recall" value={metrics.recall} />
                )}
                {metrics.accuracy !== undefined && (
                    <MetricItem label="Accuracy" value={metrics.accuracy} />
                )}
                {metrics.miou !== undefined && <MetricItem label="mIoU" value={metrics.miou} />}
            </div>
        </div>
    );
}

function parseTimestamp(timestamp: any): Date {
    if (!timestamp) return new Date();

    // Handle Firebase Timestamp formats: {_seconds, _nanoseconds} or {seconds, nanoseconds}
    const seconds = timestamp._seconds ?? timestamp.seconds;
    if (typeof seconds === "number" && !isNaN(seconds)) {
        return new Date(seconds * 1000);
    }

    // Fallback to current date if parsing fails
    return new Date();
}

export function ModelChangeInfo({ change, isLegacy = false }: ModelChangeInfoProps) {
    const timestamp = parseTimestamp(change.timestamp);
    const metrics = useCurrentModelMetrics();
    const metricsLoading = useMetricsLoading();

    return (
        <div className="flex flex-col gap-4">
            <InfoRow label="Model Type">
                {architectureLabels[change.to.architecture]}
                {change.to.variant && ` (${change.to.variant})`}
            </InfoRow>

            {/* Model metrics */}
            {metricsLoading ? (
                <div className="flex flex-col gap-2">
                    <span className="text-xs font-medium text-tertiary">Performance Metrics</span>
                    <div className="flex h-16 items-center justify-center rounded-lg bg-gray-50">
                        <span className="text-sm text-tertiary">Loading metrics...</span>
                    </div>
                </div>
            ) : metrics ? (
                <ModelMetricsDisplay metrics={metrics} />
            ) : null}

            {/* Only show training images if we have the data (not for legacy models) */}
            {!isLegacy && change.imageCountAtTraining > 0 && (
                <InfoRow label="Training Images">
                    {change.imageCountAtTraining.toLocaleString()}
                </InfoRow>
            )}

            <InfoRow label="Trained At">{timestamp.toLocaleDateString()}</InfoRow>

            {change.classes.length > 0 && (
                <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-tertiary">Classes</span>
                    <div className="flex flex-wrap gap-1">
                        {change.classes.map((cls) => (
                            <Chip key={cls} color="gray" size="small" className="text-xs">
                                {cls}
                            </Chip>
                        ))}
                    </div>
                </div>
            )}

            {change.from && (
                <InfoRow label="Previous Model">
                    {architectureLabels[change.from.architecture]}
                    {change.from.variant && ` (${change.from.variant})`}
                </InfoRow>
            )}
        </div>
    );
}
